import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RoleRight } from '@dep/common/interfaces';
import { Preferences } from '@dep/common/user-api/types/user.type';
import { TranslocoService } from '@jsverse/transloco';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';

import { LinkoutsService } from '@dep/frontend/home/services/linkouts.service';
import { UserService } from '@dep/frontend/users/services/user.service';
import { UsersService } from '@dep/frontend/users/services/users.service';

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrl: './start.component.scss',
  standalone: false,
})
export class StartComponent implements OnInit {
  public isGeneratingMKHUrl = false;
  public hasForwardPreference = false;
  public isLoading = false;

  constructor(
    private readonly linkoutsService: LinkoutsService,
    private readonly logger: NGXLogger,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly userService: UserService,
    private readonly usersService: UsersService,
    private readonly router: Router,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      const user = await this.userService.getCurrentUser();

      // Check if the user has the MAR20X Knowledge Hub role assigned.
      const mkhRole = user?.roles.find((role) => role.rights.includes(RoleRight.WEBKNOWLEDGE_READ));
      if (!mkhRole) {
        this.logger.debug('User does not have MAR20X Knowledge Hub role assigned, redirecting to home');
        void this.router.navigate(['/home']);
        return;
      }

      // Check if the user has a forward preference.
      this.hasForwardPreference = await this.userService.hasForwardPreference();
      if (this.hasForwardPreference) {
        this.logger.debug('User has forward preferences, redirecting to home');
        void this.router.navigate(['/home']);
      }
    } catch (error) {
      this.logger.error('Error fetching user', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  public async generateUrl(): Promise<void> {
    this.isGeneratingMKHUrl = true;
    try {
      const url = await this.linkoutsService.generateProductInformationPlatformUrl();
      window.open(url, '_self');
    } catch (error) {
      this.logger.error('Opening MAR20X Knowledge Hub URL failed', error);
      this.toastr.error(
        String(this.transloco.translate('HOME.QUICK_LINKS.GENERATE_URL_FAILED')) + ' (' + error + ')',
        String(this.transloco.translate('HOME.QUICK_LINKS.GENERATE_URL_FAILED_TITLE', { name: 'MAR20X Knowledge Hub' })),
      );
    } finally {
      this.isGeneratingMKHUrl = false;
    }
  }

  public async savePreferences(): Promise<void> {
    if (!this.hasForwardPreference) {
      return;
    }

    const preferences = [{
      name: 'alwaysForwardToDep',
      value: true,
      lastChanged: new Date().toISOString(),
    }] satisfies Preferences[];

    try {
      await this.usersService.updateUserProperties({ preferences });
    } catch (error) {
      this.logger.error('Error saving user preferences', error);
      this.toastr.error(
        String(this.transloco.translate('START.SAVE_ERROR')) + ' (' + error + ')',
        String(this.transloco.translate('START.SAVE_ERROR_TITLE')),
      );
    }
  }
}
